import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  MessageCircle,
  Eye,
  Monitor,
  Smartphone,
  Tablet,
  MapPin,
  Clock,
  Activity,
  User,
  Search,
  Download,
  RefreshCw,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * VisitorsTable Component - Displays visitors in tabular format
 * Sessions now include browsing visitors with status='browsing'
 */
const VisitorsTable = ({ sessions = [], onRefresh }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTimeline, setFilterTimeline] = useState('all');
  const [sortBy, setSortBy] = useState('last_seen');
  const [sortOrder, setSortOrder] = useState('desc');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // session_id to delete
  const [isDeleting, setIsDeleting] = useState(false);

  // Timer tick to control time updates every 3 seconds
  const [timeTick, setTimeTick] = useState(0);
  // Memoize current time based on tick to prevent updates between ticks
  const currentTime = React.useMemo(() => Date.now(), [timeTick]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeTick(tick => tick + 1);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Process all visitors - sessions now include browsing visitors with status='browsing'
  const allVisitors = useMemo(() => {
    logger.log('[VisitorsTable] Processing sessions:', sessions.length);

    // Mark browsing sessions with _isBrowsing flag for UI styling
    return sessions.map(session => ({
      ...session,
      _isBrowsing: session.status === 'browsing',
      // Use visitor_active from session to determine if visitor is currently on site
      is_active: session.visitor_active !== false
    }));
  }, [sessions]);

  // Delete visitor/session
  const handleDeleteVisitor = async (sessionId) => {
    try {
      setIsDeleting(true);
      await axios.delete(`${API}/session/${sessionId}`);
      setDeleteConfirm(null);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      logger.error('Error deleting visitor:', error);
      alert('Failed to delete visitor. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const getDeviceIcon = (userAgent) => {
    if (!userAgent) return Monitor;
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return Smartphone;
    }
    if (ua.includes('tablet') || ua.includes('ipad')) {
      return Tablet;
    }
    return Monitor;
  };

  const getDeviceType = (vm) => {
    if (!vm) return 'Desktop';
    return vm.device_type ? vm.device_type.charAt(0).toUpperCase() + vm.device_type.slice(1) : 'Desktop';
  };

  // Get display name for visitor based on whether they have a name, are new or returning
  const getVisitorDisplayName = (session) => {
    const vm = session.visitor_metadata || {};
    const userName = session.user_data?.name || session.user_name;

    // If visitor has provided their name, use it
    if (userName) {
      return userName;
    }

    // Check if this is a new visitor (first_visit === true) or returning (first_visit === false)
    const isNewVisitor = vm.first_visit === true;

    if (isNewVisitor) {
      // For new visitors without name, show IP address
      return vm.ip_address || 'New Visitor';
    } else {
      // For returning visitors without name, show "Customer"
      return 'Customer';
    }
  };

  const getLocation = (vm) => {
    if (!vm) return { country: 'Unknown', state: '', city: '' };

    // Use actual geolocation data from visitor metadata
    if (vm.city || vm.country) {
      return {
        country: vm.country || 'Unknown',
        state: vm.region || '',
        city: vm.city || ''
      };
    }

    // Fallback: extract from timezone if geolocation not available
    const timezone = vm.timezone || '';
    const parts = timezone.split('/');
    return {
      country: parts[0] || 'Unknown',
      state: '',
      city: parts[1]?.replace(/_/g, ' ') || ''
    };
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes < 60) return `${minutes}m ${secs}s`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getTimeOnPages = (vm, session) => {
    const activity = getActivityStatus(session);
    const visitStart = vm?.current_visit_started_at;

    // Active visitor with known visit start: live counting
    if (activity.status === 'active' && visitStart) {
      const start = new Date(visitStart).getTime();
      const duration = Math.floor((currentTime - start) / 1000);
      return formatDuration(Math.max(0, duration));
    }

    // Idle/Away visitor with known visit start: compute from visit start to last heartbeat
    if (visitStart && session?.updated_at) {
      const start = new Date(visitStart).getTime();
      const end = new Date(session.updated_at).getTime();
      const duration = Math.floor((end - start) / 1000);
      return formatDuration(Math.max(0, duration));
    }

    // Fallback for old sessions without current_visit_started_at
    return formatDuration(vm?.session_duration || 0);
  };

  const getLastSeen = (updatedAt) => {
    if (!updatedAt) return 'Unknown';
    const now = new Date();
    const updated = new Date(updatedAt);
    const diffMs = now - updated;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const getActivityStatus = (session) => {
    // If visitor explicitly left (closed tab), show as Away immediately
    if (session.is_active === false) {
      return { status: 'away', label: 'Away', color: 'bg-gray-400' };
    }

    const lastSeen = new Date(session.updated_at);
    const now = new Date();
    const diffMins = Math.floor((now - lastSeen) / 60000);

    if (diffMins < 5) return { status: 'active', label: 'Active', color: 'bg-green-500' };
    if (diffMins < 30) return { status: 'idle', label: 'Idle', color: 'bg-yellow-500' };
    return { status: 'away', label: 'Away', color: 'bg-gray-400' };
  };

  const getChattingWith = (session) => {
    // Browsing-only visitors (status='browsing')
    if (session._isBrowsing || session.status === 'browsing') {
      return 'Just Browsing';
    }
    if (session.status === 'agent_active' && session.assigned_agent) {
      return 'Live Agent';
    }
    if (session.status === 'waiting_for_agent') {
      return 'In Queue';
    }
    if (session.message_count > 0) {
      return 'AI Assistant';
    }
    return 'Browsing';
  };

  const getCameFrom = (vm) => {
    if (!vm) return 'Direct';
    if (vm.referrer && vm.referrer !== 'Direct') {
      try {
        const url = new URL(vm.referrer);
        return url.hostname;
      } catch (e) {
        return vm.referrer;
      }
    }
    return 'Direct';
  };

  // Timeline filter helper - checks if session falls within the selected time range
  const matchesTimeline = (session) => {
    if (filterTimeline === 'all') return true;

    const sessionDate = new Date(session.created_at || session.updated_at);
    const now = new Date();

    // Get start of today
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Get start of yesterday
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    // Get start of this week (Sunday)
    const startOfThisWeek = new Date(startOfToday);
    startOfThisWeek.setDate(startOfThisWeek.getDate() - startOfThisWeek.getDay());

    // Get start of last 7 days
    const last7Days = new Date(startOfToday);
    last7Days.setDate(last7Days.getDate() - 7);

    // Get start of last 30 days
    const last30Days = new Date(startOfToday);
    last30Days.setDate(last30Days.getDate() - 30);

    // Get start of this month
    const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    // Get start of last month
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

    switch (filterTimeline) {
      case 'today':
        return sessionDate >= startOfToday;
      case 'yesterday':
        return sessionDate >= startOfYesterday && sessionDate < startOfToday;
      case 'this_week':
        return sessionDate >= startOfThisWeek;
      case 'last_7_days':
        return sessionDate >= last7Days;
      case 'last_30_days':
        return sessionDate >= last30Days;
      case 'this_month':
        return sessionDate >= startOfThisMonth;
      case 'last_month':
        return sessionDate >= startOfLastMonth && sessionDate <= endOfLastMonth;
      default:
        return true;
    }
  };

  // Filter and sort all visitors (sessions + browsing visitors)
  const processedSessions = allVisitors
    .filter(session => {
      // Filter by search term (check name, email, IP address, and visitor_id for browsing visitors)
      const visitorName = getVisitorDisplayName(session);
      const visitorEmail = session.user_data?.email || session.user_email || '';
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = visitorName.toLowerCase().includes(searchLower) ||
                           visitorEmail.toLowerCase().includes(searchLower) ||
                           (session.visitor_id || '').toLowerCase().includes(searchLower);

      // Filter by status
      const activity = getActivityStatus(session);
      const matchesStatusFilter = filterStatus === 'all' || activity.status === filterStatus;

      // Filter by timeline
      const matchesTimelineFilter = matchesTimeline(session);

      return matchesSearch && matchesStatusFilter && matchesTimelineFilter;
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = getVisitorDisplayName(a).toLowerCase();
          bValue = getVisitorDisplayName(b).toLowerCase();
          break;
        case 'last_seen':
          aValue = new Date(a.updated_at);
          bValue = new Date(b.updated_at);
          break;
        case 'chats':
          aValue = a.message_count || 0;
          bValue = b.message_count || 0;
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Activity', 'Status', 'Time on Pages', 'Country', 'City', 'Came From', 'Device', 'Last Seen', 'Last Page', 'Chats'];

    const rows = processedSessions.map(session => {
      const vm = session.visitor_metadata || {};
      const location = getLocation(vm);
      return [
        getVisitorDisplayName(session),
        session.user_data?.email || session.user_email || '',
        getActivityStatus(session).label,
        getChattingWith(session),
        getTimeOnPages(vm, session),
        location.country,
        location.city,
        getCameFrom(vm),
        getDeviceType(vm),
        getLastSeen(session.updated_at),
        vm.current_page || '/',
        session.message_count || 0
      ].map(val => `"${val}"`).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visitors-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4">
      {/* Header with Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Visitors</p>
                <h3 className="text-2xl font-bold text-gray-900">
                  {allVisitors.filter(matchesTimeline).length}
                </h3>
              </div>
              <User className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Now</p>
                <h3 className="text-2xl font-bold text-gray-900">
                  {allVisitors.filter(s => matchesTimeline(s) && getActivityStatus(s).status === 'active').length}
                </h3>
              </div>
              <Activity className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">In Chat</p>
                <h3 className="text-2xl font-bold text-gray-900">
                  {allVisitors.filter(s => matchesTimeline(s) && s.message_count > 0).length}
                </h3>
              </div>
              <MessageCircle className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-cyan-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Just Browsing</p>
                <h3 className="text-2xl font-bold text-gray-900">
                  {allVisitors.filter(s => matchesTimeline(s) && s._isBrowsing).length}
                </h3>
              </div>
              <Eye className="w-8 h-8 text-cyan-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">First-Time</p>
                <h3 className="text-2xl font-bold text-gray-900">
                  {allVisitors.filter(s => matchesTimeline(s) && s.visitor_metadata?.first_visit).length}
                </h3>
              </div>
              <Eye className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-1 gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search visitors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="idle">Idle</option>
                <option value="away">Away</option>
              </select>

              <select
                value={filterTimeline}
                onChange={(e) => setFilterTimeline(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
              </select>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={onRefresh}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>

              <Button
                onClick={exportToCSV}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activity
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Chatting With
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time on Pages
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Came From
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('last_seen')}
                  >
                    Last Seen {sortBy === 'last_seen' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Page
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('chats')}
                  >
                    Chats {sortBy === 'chats' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {processedSessions.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="px-4 py-8 text-center text-gray-500">
                      <User className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No visitors found</p>
                    </td>
                  </tr>
                ) : (
                  processedSessions.map((session) => {
                    const vm = session.visitor_metadata || {};
                    const DeviceIcon = getDeviceIcon(vm.user_agent);
                    const activity = getActivityStatus(session);
                    const location = getLocation(vm);
                    const visitorName = getVisitorDisplayName(session);
                    const visitorEmail = session.user_data?.email || session.user_email || '';
                    const isBrowsing = session._isBrowsing;
                    // Only show "Browsing" badge if:
                    // 1. status is 'browsing' (not chatting)
                    // 2. Activity is "Active" (not Idle or Away)
                    const isActivelyBrowsing = isBrowsing && activity.status === 'active';

                    return (
                      <tr
                        key={session.session_id || session.visitor_id || session.id}
                        className={`hover:bg-gray-50 transition-colors ${isActivelyBrowsing ? 'bg-cyan-50/30' : ''}`}
                      >
                        {/* Name */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm ${isActivelyBrowsing ? 'bg-gradient-to-br from-cyan-500 to-teal-500' : 'bg-gradient-to-br from-blue-500 to-purple-500'}`}>
                              {visitorName[0].toUpperCase()}
                            </div>
                            <span className="font-medium text-gray-900">
                              {visitorName}
                            </span>
                            {isActivelyBrowsing && (
                              <Badge variant="outline" className="text-xs text-cyan-600 border-cyan-300">
                                Browsing
                              </Badge>
                            )}
                          </div>
                        </td>

                        {/* Email */}
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {visitorEmail || '—'}
                        </td>

                        {/* Activity */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${activity.color}`}></div>
                            <span className="text-sm text-gray-600">{activity.label}</span>
                          </div>
                        </td>

                        {/* Chatting With */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Badge variant="outline" className="text-xs">
                            {getChattingWith(session)}
                          </Badge>
                        </td>

                        {/* Time on Pages */}
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {getTimeOnPages(vm, session)}
                        </td>

                        {/* Location */}
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-gray-400" />
                            <span>
                              {location.city && location.country !== 'Unknown'
                                ? `${location.city}, ${location.country}`
                                : location.country}
                            </span>
                          </div>
                        </td>

                        {/* Came From */}
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {getCameFrom(vm)}
                        </td>

                        {/* Device */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <DeviceIcon className="w-4 h-4 text-gray-400" />
                            <span>{getDeviceType(vm)}</span>
                          </div>
                        </td>

                        {/* Last Seen */}
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-gray-400" />
                            {getLastSeen(session.updated_at)}
                          </div>
                        </td>

                        {/* Last Page */}
                        <td
                          className="px-4 py-3 whitespace-nowrap text-sm max-w-xs truncate"
                          title={vm.current_page || '/'}
                        >
                          <a
                            href={vm.current_page || '/'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {vm.current_page_title || vm.current_page || '/'}
                          </a>
                        </td>

                        {/* Chats */}
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                          {session.message_count || 0}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => navigate(`/admin/visitor/${session.session_id || `visitor:${session.visitor_id}`}`)}
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-1"
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </Button>
                            {session.session_id && (
                              <Button
                                onClick={() => setDeleteConfirm(session.session_id)}
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Visitor</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this visitor? This will permanently remove all their data including chat history and session information.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                onClick={() => setDeleteConfirm(null)}
                variant="outline"
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteVisitor(deleteConfirm)}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="text-sm text-gray-500 text-center">
        Showing {processedSessions.length} of {allVisitors.filter(matchesTimeline).length} visitors
        {filterTimeline !== 'all' && ` (${filterTimeline.replace(/_/g, ' ')})`}
      </div>
    </div>
  );
};

export default VisitorsTable;
